/**
 * `react-native-image-picker`, as a file input.
 *
 * `launchImageLibrary` becomes a plain `<input type="file">`;
 * `launchCamera` adds `capture="environment"`, which mobile browsers answer
 * by opening the camera and desktop browsers quietly treat as the same file
 * dialog - the honest best each platform offers.
 *
 * The downscale the native module does (`maxWidth`/`maxHeight`/`quality`)
 * is reproduced with a canvas, because it exists for a reason that is
 * platform-blind: these photos are thumbnails and evidence, and a 12MP
 * original helps nobody. Results come back as object URLs in the same
 * `{ assets: [{ uri }] }` envelope `services/photos.ts` reads; like the
 * native `file://` URIs they stand in for, they are local to this session.
 */

type PickerOptions = {
  mediaType?: string;
  selectionLimit?: number;
  saveToPhotos?: boolean;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
};

type Asset = {
  uri: string;
  fileName?: string;
  type?: string;
  width?: number;
  height?: number;
};

type PickerResult = { assets?: Asset[]; didCancel?: boolean };

/** Downscale one file the way the native picker would, or pass it through. */
const toAsset = (file: File, options?: PickerOptions): Promise<Asset> =>
  new Promise(resolve => {
    const passThrough = (): void =>
      resolve({
        uri: URL.createObjectURL(file),
        fileName: file.name,
        type: file.type,
      });

    const maxWidth = options?.maxWidth ?? Infinity;
    const maxHeight = options?.maxHeight ?? Infinity;

    const image = new Image();
    const sourceUrl = URL.createObjectURL(file);

    image.onload = () => {
      const scale = Math.min(
        1,
        maxWidth / image.naturalWidth,
        maxHeight / image.naturalHeight,
      );

      if (scale >= 1) {
        URL.revokeObjectURL(sourceUrl);
        passThrough();
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = Math.round(image.naturalWidth * scale);
      canvas.height = Math.round(image.naturalHeight * scale);
      const context = canvas.getContext('2d');
      if (!context) {
        URL.revokeObjectURL(sourceUrl);
        passThrough();
        return;
      }
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        blob => {
          URL.revokeObjectURL(sourceUrl);
          if (!blob) {
            passThrough();
            return;
          }
          resolve({
            uri: URL.createObjectURL(blob),
            fileName: file.name.replace(/\.\w+$/, '.jpg'),
            type: 'image/jpeg',
            width: canvas.width,
            height: canvas.height,
          });
        },
        'image/jpeg',
        options?.quality ?? 0.8,
      );
    };

    image.onerror = () => {
      URL.revokeObjectURL(sourceUrl);
      passThrough();
    };

    image.src = sourceUrl;
  });

const pick = (
  options?: PickerOptions,
  capture?: boolean,
): Promise<PickerResult> =>
  new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if ((options?.selectionLimit ?? 1) !== 1) {
      input.multiple = true;
    }
    if (capture) {
      input.setAttribute('capture', 'environment');
    }
    input.style.display = 'none';
    document.body.appendChild(input);

    const finish = (result: PickerResult): void => {
      input.remove();
      resolve(result);
    };

    input.onchange = () => {
      const limit = options?.selectionLimit || undefined;
      const files = Array.from(input.files ?? []).slice(0, limit);
      if (files.length === 0) {
        finish({ didCancel: true });
        return;
      }
      Promise.all(files.map(file => toAsset(file, options))).then(assets =>
        finish({ assets }),
      );
    };

    // Fired by modern browsers when the dialog is dismissed; older ones
    // simply never resolve this promise, which the callers' `.catch(() =>
    // null)` treats as an empty pick anyway.
    input.oncancel = () => finish({ didCancel: true });

    input.click();
  });

export const launchImageLibrary = (
  options?: PickerOptions,
): Promise<PickerResult> => pick(options);

export const launchCamera = (
  options?: PickerOptions,
): Promise<PickerResult> =>
  pick({ ...options, selectionLimit: 1 }, true);
